import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Input, Modal, Text } from '@viapeople/components'
import { useState } from 'react'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type { CrmProvider } from '../types'

interface CreateConnectionModalProps {
	isOpen: boolean
	onClose: () => void
	organizationId: string
	onComplete: () => void
}

export function CreateConnectionModal({ isOpen, onClose, organizationId, onComplete }: CreateConnectionModalProps) {
	const { showToast } = useToast()
	const [name, setName] = useState('')
	const [provider, setProvider] = useState<CrmProvider>('salesforce')
	const [clientId, setClientId] = useState('')
	const [clientSecret, setClientSecret] = useState('')
	const [instanceUrl, setInstanceUrl] = useState('https://login.salesforce.com')
	const [baseUrl, setBaseUrl] = useState('')
	const [createdConnectionId, setCreatedConnectionId] = useState<string | null>(null)
	const [dealCloudAuthed, setDealCloudAuthed] = useState(false)
	const [selectedEntryTypeId, setSelectedEntryTypeId] = useState<number | null>(null)

	const createMutation = useMutation({
		...trpc.admin.integrations.crmConnection.create.mutationOptions(),
		onSuccess: (data) => {
			setCreatedConnectionId(data.id)

			// For DealCloud, immediately authenticate (no OAuth redirect needed)
			if (provider === 'dealcloud') {
				authenticateDealCloudMutation.mutate({ connectionId: data.id })
			}
		},
		onError: (err) => showToast(`Failed to create connection: ${err.message}`, 'error'),
	})

	const authenticateDealCloudMutation = useMutation({
		...trpc.admin.integrations.crmConnection.authenticateDealCloud.mutationOptions(),
		onSuccess: () => {
			setDealCloudAuthed(true)
		},
		onError: (err) => {
			showToast(`DealCloud authentication failed: ${err.message}`, 'error')
		},
	})

	const {
		data: entryTypes,
		isLoading: isLoadingEntryTypes,
		error: entryTypesError,
	} = useQuery({
		...trpc.admin.integrations.crmConnection.listDealCloudEntryTypes.queryOptions({
			connectionId: createdConnectionId as string,
		}),
		enabled: !!createdConnectionId && provider === 'dealcloud' && dealCloudAuthed,
	})

	const setEntryTypeMutation = useMutation({
		...trpc.admin.integrations.crmConnection.setDealCloudEntryType.mutationOptions(),
		onSuccess: () => {
			showToast('Entry type saved. DealCloud connection is ready.')
			onComplete()
		},
		onError: (err) => showToast(`Failed to save entry type: ${err.message}`, 'error'),
	})

	// Once we have a connectionId, fetch the authorization URL to start OAuth (Salesforce only)
	const { data: authUrlData, isLoading: isLoadingAuthUrl } = useQuery({
		...trpc.admin.integrations.crmConnection.getAuthorizationUrl.queryOptions({
			organizationId,
			connectionId: createdConnectionId as string,
			adminUrl: `${window.location.origin}${window.location.pathname}`,
		}),
		enabled: !!createdConnectionId && provider === 'salesforce',
	})

	const handleSubmit = () => {
		if (!name || !clientId || !clientSecret) {
			showToast('All fields are required', 'error')
			return
		}

		if (provider === 'salesforce' && !instanceUrl) {
			showToast('Instance URL is required', 'error')
			return
		}

		if (provider === 'dealcloud' && !baseUrl) {
			showToast('Base URL is required', 'error')
			return
		}

		// Credentials are encrypted server-side by CrmConnectionService.encryptCredentials()
		createMutation.mutate({
			organizationId,
			name,
			provider,
			credentials: {
				clientId,
				clientSecret,
			},
			metadata: provider === 'dealcloud' ? { baseUrl } : { instanceUrl },
		})
	}

	const handleAuthorize = () => {
		if (authUrlData?.url) {
			window.location.href = authUrlData.url
		}
	}

	const handleSkipOAuth = () => {
		showToast('Connection saved. You can authorize later from the Connections tab.')
		onComplete()
	}

	const isSaving = createMutation.isPending || authenticateDealCloudMutation.isPending || setEntryTypeMutation.isPending

	// DealCloud post-create flow: authenticate → pick entry type
	if (createdConnectionId && provider === 'dealcloud') {
		// Phase 1: still authenticating, or auth failed
		if (!dealCloudAuthed) {
			return (
				<Modal isOpen={isOpen} onClose={onClose} title='Authenticating DealCloud' className='!w-[500px]'>
					<div className='flex flex-col gap-4'>
						<div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
							<Text variant='small' weight='semibold' className='text-blue-900'>
								{authenticateDealCloudMutation.isPending
									? 'Authenticating with DealCloud...'
									: authenticateDealCloudMutation.isError
										? 'Authentication failed'
										: 'Connection authenticated'}
							</Text>
							<Text variant='xsmall' className='text-blue-700 mt-1'>
								{authenticateDealCloudMutation.isPending
									? 'Requesting access token using client credentials...'
									: authenticateDealCloudMutation.isError
										? 'Please check your credentials and try again.'
										: 'DealCloud connection is ready to use.'}
							</Text>
						</div>

						{authenticateDealCloudMutation.isError && (
							<div className='flex justify-end gap-2 mt-4'>
								<Button variant='secondary' onClick={onClose}>
									Close
								</Button>
								<Button
									variant='primary'
									onClick={() => authenticateDealCloudMutation.mutate({ connectionId: createdConnectionId })}>
									Retry
								</Button>
							</div>
						)}
					</div>
				</Modal>
			)
		}

		// Phase 2: pick the entry type that holds deals
		return (
			<Modal isOpen={isOpen} onClose={onClose} title='Pick Your Deal Entry Type' className='!w-[560px]'>
				<div className='flex flex-col gap-4'>
					<div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
						<Text variant='small' weight='semibold' className='text-blue-900'>
							Which DealCloud entry type holds your deals?
						</Text>
						<Text variant='xsmall' className='text-blue-700 mt-1'>
							DealCloud schemas are firm-specific. Pick the entry type whose rows represent the deals you want viaPeople
							to track (e.g. <em>Deal</em>, <em>Mandate</em>, <em>Prospect / Engagement</em>).
						</Text>
					</div>

					{isLoadingEntryTypes && (
						<Text variant='xsmall' className='text-neutral-500'>
							Loading entry types from DealCloud…
						</Text>
					)}

					{entryTypesError && (
						<div className='p-3 bg-red-50 border border-red-200 rounded-lg'>
							<Text variant='small' weight='semibold' className='text-red-900'>
								Couldn't load entry types
							</Text>
							<Text variant='xsmall' className='text-red-700 mt-1'>
								{entryTypesError.message}
							</Text>
						</div>
					)}

					{entryTypes?.length ? (
						<div className='flex flex-col gap-1'>
							<Text variant='small' weight='semibold' className='text-neutral-700'>
								Deal entry type
							</Text>
							<select
								className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm'
								value={selectedEntryTypeId ?? ''}
								onChange={(e) => setSelectedEntryTypeId(e.target.value ? Number(e.target.value) : null)}>
								<option value=''>Select an entry type…</option>
								{entryTypes.map((et) => (
									<option key={et.id} value={et.id}>
										{et.name} (id: {et.id})
									</option>
								))}
							</select>
						</div>
					) : null}

					{entryTypes?.length === 0 && (
						<Text variant='xsmall' className='text-red-700'>
							No non-system entry types found in this DealCloud site. Check that the API key has schema-read access.
						</Text>
					)}

					<div className='flex justify-end gap-2 mt-4'>
						{/* Once entry types load, picking one is required — skipping persists a connection
						    without dealEntryTypeId, which the next poll fails on with a confusing runtime
						    error. If the discovery call failed (no entryTypes), keep Skip enabled as an
						    escape hatch so the admin isn't trapped in the modal. */}
						<Button
							variant='secondary'
							onClick={onComplete}
							disabled={isSaving || isLoadingEntryTypes || (entryTypes?.length ?? 0) > 0}
							title={
								isLoadingEntryTypes
									? 'Wait for entry types to load before skipping.'
									: (entryTypes?.length ?? 0) > 0
										? 'Pick a deal entry type to finish setup. Skipping leaves the connection unusable.'
										: undefined
							}>
							Skip for Now
						</Button>
						<Button
							variant='primary'
							disabled={!selectedEntryTypeId || isSaving}
							onClick={() => {
								if (!selectedEntryTypeId) return
								setEntryTypeMutation.mutate({
									connectionId: createdConnectionId,
									entryTypeId: selectedEntryTypeId,
								})
							}}>
							{setEntryTypeMutation.isPending ? 'Saving…' : 'Save & Finish'}
						</Button>
					</div>
				</div>
			</Modal>
		)
	}

	// After Salesforce connection is created, show OAuth prompt
	if (createdConnectionId && provider === 'salesforce') {
		return (
			<Modal isOpen={isOpen} onClose={onClose} title='Authorize Connection' className='!w-[500px]'>
				<div className='flex flex-col gap-4'>
					<div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
						<Text variant='small' weight='semibold' className='text-green-900'>
							Connection created successfully
						</Text>
						<Text variant='xsmall' className='text-green-700 mt-1'>
							Now authorize with Salesforce to complete the setup. You will be redirected to Salesforce to grant access.
						</Text>
					</div>

					<div className='flex justify-end gap-2 mt-4'>
						<Button variant='secondary' onClick={handleSkipOAuth}>
							Skip for Now
						</Button>
						<Button variant='primary' onClick={handleAuthorize} disabled={isLoadingAuthUrl || !authUrlData?.url}>
							{isLoadingAuthUrl ? 'Preparing...' : 'Authorize with Salesforce'}
						</Button>
					</div>
				</div>
			</Modal>
		)
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} title='Add New Connection' className='!w-[500px]'>
			<div className='flex flex-col gap-4'>
				<Input
					label='Connection Name'
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder={provider === 'dealcloud' ? 'e.g. Production DealCloud' : 'e.g. Production Salesforce'}
					isRequired
				/>

				<div className='flex flex-col gap-1'>
					<Text variant='small' weight='semibold' className='text-neutral-700'>
						Provider
					</Text>
					<select
						className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm'
						value={provider}
						onChange={(e) => setProvider(e.target.value as CrmProvider)}>
						<option value='salesforce'>Salesforce</option>
						<option value='dealcloud'>DealCloud</option>
					</select>
				</div>

				{provider === 'salesforce' && (
					<div className='border-t border-neutral-100 my-2 pt-4'>
						<Text variant='small' weight='semibold' className='mb-2'>
							Salesforce Connected App Credentials
						</Text>
						<Text variant='xsmall' className='text-neutral-500 mb-3'>
							Enter the credentials from your Salesforce Connected App. These are encrypted at rest on the server.
						</Text>
						<Input
							label='Instance URL'
							value={instanceUrl}
							onChange={(e) => setInstanceUrl(e.target.value)}
							placeholder='https://login.salesforce.com'
							isRequired
						/>
						<div className='mt-2'>
							<Input
								label='Client ID (Consumer Key)'
								value={clientId}
								onChange={(e) => setClientId(e.target.value)}
								placeholder='Enter Consumer Key'
								isRequired
							/>
						</div>
						<div className='mt-2'>
							<Input
								label='Client Secret (Consumer Secret)'
								type='password'
								value={clientSecret}
								onChange={(e) => setClientSecret(e.target.value)}
								placeholder='Enter Consumer Secret'
								isRequired
							/>
						</div>
					</div>
				)}

				{provider === 'dealcloud' && (
					<div className='border-t border-neutral-100 my-2 pt-4'>
						<Text variant='small' weight='semibold' className='mb-2'>
							DealCloud API Credentials
						</Text>
						<Text variant='xsmall' className='text-neutral-500 mb-3'>
							Enter your DealCloud site URL and API credentials. These are encrypted at rest on the server.
						</Text>
						<Input
							label='Base URL'
							value={baseUrl}
							onChange={(e) => setBaseUrl(e.target.value)}
							placeholder='https://yoursite.dealcloud.com'
							isRequired
						/>
						<div className='mt-2'>
							<Input
								label='Client ID'
								value={clientId}
								onChange={(e) => setClientId(e.target.value)}
								placeholder='Enter Client ID'
								isRequired
							/>
						</div>
						<div className='mt-2'>
							<Input
								label='Client Secret (API Key)'
								type='password'
								value={clientSecret}
								onChange={(e) => setClientSecret(e.target.value)}
								placeholder='Enter Client Secret'
								isRequired
							/>
						</div>
					</div>
				)}

				<div className='flex justify-end gap-2 mt-4'>
					<Button variant='secondary' onClick={onClose} disabled={isSaving}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleSubmit} disabled={isSaving}>
						{isSaving ? 'Saving...' : provider === 'dealcloud' ? 'Save & Authenticate' : 'Save & Continue to OAuth'}
					</Button>
				</div>
			</div>
		</Modal>
	)
}
